/**
 * Delaunay triangulation — Bowyer–Watson, self-contained pure math
 * (no three.js / DOM / library dependency, unit-testable like PcaRefine).
 *
 * Input: 2D points (screen-space pixel coords of the pick window).
 * Output: triangles as index triples into the input array.
 *
 * Degenerate inputs (fewer than 3 usable points, all collinear,
 * duplicates) simply yield fewer/no triangles — callers treat an empty
 * result as "no mesh" and fall back.
 */

export interface IPoint2 {
  x: number;
  y: number;
}

interface ITri {
  a: number;
  b: number;
  c: number;
}

const EPS = 1e-9;

/** signed double-area of triangle ABC (>0 = counter-clockwise) */
const orient = (A: IPoint2, B: IPoint2, C: IPoint2): number =>
  (B.x - A.x) * (C.y - A.y) - (B.y - A.y) * (C.x - A.x);

/** true when point P lies strictly inside the circumcircle of ABC */
const inCircumcircle = (
  A: IPoint2,
  B: IPoint2,
  C: IPoint2,
  P: IPoint2
): boolean => {
  const ax = A.x - P.x;
  const ay = A.y - P.y;
  const bx = B.x - P.x;
  const by = B.y - P.y;
  const cx = C.x - P.x;
  const cy = C.y - P.y;
  const det =
    (ax * ax + ay * ay) * (bx * cy - cx * by) -
    (bx * bx + by * by) * (ax * cy - cx * ay) +
    (cx * cx + cy * cy) * (ax * by - bx * ay);
  // determinant sign depends on triangle orientation
  return orient(A, B, C) > 0 ? det > EPS : det < -EPS;
};

/**
 * Triangulate a 2D point set. Returns index triples into `points`.
 * Duplicate points (within EPS) are inserted once; degenerate triangles
 * are dropped from the result.
 */
export const triangulate = (
  points: IPoint2[]
): [number, number, number][] => {
  const n = points.length;
  if (n < 3) return [];

  // super-triangle large enough to contain every input point
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const dmax = Math.max(maxX - minX, maxY - minY, 1);
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  const pts: IPoint2[] = points.slice();
  const s0 = pts.length;
  pts.push({ x: midX - 20 * dmax, y: midY - dmax });
  const s1 = pts.length;
  pts.push({ x: midX, y: midY + 20 * dmax });
  const s2 = pts.length;
  pts.push({ x: midX + 20 * dmax, y: midY - dmax });

  let tris: ITri[] = [{ a: s0, b: s1, c: s2 }];

  for (let i = 0; i < n; i++) {
    const p = pts[i];

    // skip exact duplicates of an earlier point — they carry no new area
    // and break the incircle test
    let dup = false;
    for (let j = 0; j < i; j++) {
      if (Math.abs(pts[j].x - p.x) < EPS && Math.abs(pts[j].y - p.y) < EPS) {
        dup = true;
        break;
      }
    }
    if (dup) continue;

    // Bowyer–Watson step: carve out every triangle whose circumcircle
    // contains p, then stitch p to the boundary of the hole
    const keep: ITri[] = [];
    const bad: ITri[] = [];
    for (const t of tris) {
      (inCircumcircle(pts[t.a], pts[t.b], pts[t.c], p) ? bad : keep).push(t);
    }

    // hole boundary = edges appearing in exactly ONE carved triangle
    const edges: [number, number][] = [];
    const addEdge = (u: number, v: number): void => {
      for (let k = 0; k < edges.length; k++) {
        const [eu, ev] = edges[k];
        if ((eu === u && ev === v) || (eu === v && ev === u)) {
          edges.splice(k, 1); // shared edge → interior → cancels out
          return;
        }
      }
      edges.push([u, v]);
    };
    for (const t of bad) {
      addEdge(t.a, t.b);
      addEdge(t.b, t.c);
      addEdge(t.c, t.a);
    }
    for (const [u, v] of edges) keep.push({ a: u, b: v, c: i });
    tris = keep;
  }

  // drop triangles that touch the super-triangle or are degenerate
  const out: [number, number, number][] = [];
  for (const t of tris) {
    if (t.a >= n || t.b >= n || t.c >= n) continue;
    if (Math.abs(orient(pts[t.a], pts[t.b], pts[t.c])) < EPS) continue;
    out.push([t.a, t.b, t.c]);
  }
  return out;
};
